<script>
  import { Modal as DangerModal } from "carbon-components-svelte";
  import { InfoBlock, TextInput } from "paxray-components-svelte";
  import DataTableBlack from "@components/DataTableBlack.svelte";
  import AppIcon from "./AppIcon.svelte";
  import Modal from "@components/Modal.svelte";
  import ContentSwitcher from "lib/components/ContentSwitcher.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import {
    appProcessName,
    isValidWebsite,
    presetUrlToDomain,
    websitePattern,
    windowTitlePattern,
    windowTitleText,
  } from "lib/util/restrictionRules.js";
  import EditIcon from "../../settings/svg/EditIcon.svelte";
  import TrashCan16 from "carbon-icons-svelte/lib/TrashCan16";

  // Exclusions participants entered on the restrictions page (userlimitations).
  // Shown below the project exclusions, as a hint only while there are none.
  export let idproject;
  // Editing and deleting are only allowed until "Complete & Lock Exclusions"
  export let editable = false;
  // true while an entry is saved or deleted, for the SaveStatus of the page
  export let saving = false;
  // Number of entries for the page, null until loaded
  export let entryCount = null;

  // Bindable, the project exclusions use them to block entries a participant already added
  export let entries = [];
  let loaded = false;
  let page = 1;
  let pageSize = 10;
  let searchValue = "";
  let typeFilter = 0;
  const typeFilterOptions = ["All", "Apps", "Websites"];
  let entryToDelete = null;
  let deleteConfirmationOpen = false;
  let editModalOpen = false;
  let editEntry = null;
  let editError = "";

  $: if (idproject) loadEntries(idproject);

  async function loadEntries(projectId) {
    try {
      const response = await fetchWithSession(
        `/api/damprojects/project-user-limitations/${projectId}`,
      );
      if (!response.ok) throw new Error("Could not load participant exclusions");
      // Saved as recorder patterns, shown and edited as plain text
      entries = (await response.json()).map((entry) => ({
        ...entry,
        windowtitleorurl: entry.isbrowser
          ? presetUrlToDomain(entry.windowtitleorurl)
          : windowTitleText(entry.windowtitleorurl),
      }));
    } catch (error) {
      console.error("Error loading participant exclusions:", error);
      entries = [];
    } finally {
      loaded = true;
    }
  }

  $: entryCount = loaded ? entries.length : null;

  $: filteredEntries = entries.filter((row) => {
    const matchesType =
      typeFilter === 0 ||
      (typeFilter === 1 && !row.isbrowser) ||
      (typeFilter === 2 && row.isbrowser);
    if (!matchesType) return false;
    const query = searchValue.trim().toLowerCase();
    if (!query) return true;
    return [row.name, row.appname, row.windowtitleorurl, row.reason].some(
      (value) =>
        String(value || "")
          .toLowerCase()
          .includes(query),
    );
  });

  $: tableHeaders = [
    { key: "name", value: "App/Website" },
    { key: "appname", value: "Technical Name/URL" },
    { key: "windowtitle", value: "Window Title" },
    { key: "reason", value: "Reason" },
    ...(editable ? [{ key: "menu", value: "Actions" }] : []),
  ];

  const columnActions = {
    menu: [
      {
        icon: EditIcon,
        label: "Edit",
        onClick: (row) => openEdit(row),
      },
      {
        icon: TrashCan16,
        label: "Delete",
        danger: true,
        onClick: (row) => {
          entryToDelete = row;
          deleteConfirmationOpen = true;
        },
      },
    ],
  };

  function appIconUrl(row) {
    const application = row.isbrowser ? row.windowtitleorurl : row.appname;
    return application
      ? `/api/damprojects/app-icon?application=${encodeURIComponent(application)}`
      : "";
  }

  function openEdit(row) {
    editEntry = { ...row };
    editError = "";
    editModalOpen = true;
  }

  function closeEdit() {
    editModalOpen = false;
    editEntry = null;
  }

  function normalizedEdit(entry) {
    return {
      name: String(entry?.name || "").trim(),
      appname: entry?.isbrowser ? "" : appProcessName(entry?.appname),
      windowtitleorurl: String(entry?.windowtitleorurl || "").trim(),
      reason: String(entry?.reason || "").trim(),
    };
  }

  // Save stays locked while the entry cannot be saved, the tooltip names the reason.
  $: editBlockedReason = (() => {
    if (!editEntry) return "";
    const edited = normalizedEdit(editEntry);
    if (!edited.name) return "Enter a name first.";
    if (editEntry.isbrowser) {
      if (!edited.windowtitleorurl) return "Enter the website address first.";
      if (!isValidWebsite(edited.windowtitleorurl))
        return "Enter a valid website address, e.g. amazon.com";
    } else if (!edited.appname) {
      return "Enter the application name first.";
    }
    const duplicate = entries.some(
      (entry) =>
        entry.id !== editEntry.id &&
        String(entry.name).toLowerCase() === edited.name.toLowerCase() &&
        String(entry.appname).toLowerCase() === edited.appname.toLowerCase() &&
        String(entry.windowtitleorurl).toLowerCase() ===
          edited.windowtitleorurl.toLowerCase(),
    );
    return duplicate ? "This entry already exists." : "";
  })();

  async function saveEdit() {
    if (!editEntry || editBlockedReason) return;
    const edited = normalizedEdit(editEntry);
    saving = true;
    try {
      const response = await fetchWithSession(
        `/api/damprojects/project-user-limitations/${idproject}/${editEntry.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...edited,
            windowtitleorurl: editEntry.isbrowser
              ? websitePattern(edited.windowtitleorurl)
              : windowTitlePattern(edited.windowtitleorurl),
          }),
          suppressSuccessToast: true,
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        editError = data.message || "The entry could not be saved.";
        return;
      }
      entries = entries.map((entry) =>
        entry.id === editEntry.id ? { ...entry, ...edited } : entry,
      );
      closeEdit();
    } catch (error) {
      console.error("Error saving participant exclusion:", error);
      editError = "The entry could not be saved.";
    } finally {
      saving = false;
    }
  }

  function closeDeleteConfirmation() {
    deleteConfirmationOpen = false;
    entryToDelete = null;
  }

  async function confirmDelete() {
    if (!entryToDelete?.id) return;
    saving = true;
    try {
      const response = await fetchWithSession(
        `/api/damprojects/project-user-limitations/${idproject}/${entryToDelete.id}`,
        { method: "DELETE", suppressSuccessToast: true },
      );
      if (!response.ok) throw new Error("Could not delete participant exclusion");
      entries = entries.filter((entry) => entry.id !== entryToDelete.id);
    } catch (error) {
      console.error("Error deleting participant exclusion:", error);
    } finally {
      saving = false;
      closeDeleteConfirmation();
    }
  }
</script>

{#if loaded && !entries.length}
  <div data-guide="participant-exclusions">
    <InfoBlock
      type="info"
      heading="Participant exclusions (optional)"
      message="No participants have added optional exclusions."
      showClose={false}
    />
  </div>
{:else if entries.length}
  <InfoBlock
    type="info"
    heading="Participant exclusions (optional)"
    message="Apps and websites your participants excluded on the participant page."
    showClose={false}
  />

  <div data-guide="participant-exclusions">
    <DataTableBlack
      headers={tableHeaders}
      rows={filteredEntries}
      totalItems={filteredEntries.length}
      bind:page
      bind:pageSize
      pageSizes={[10, 25, 50]}
      {searchValue}
      slicePage
      sortable
      showNewButton={false}
      emptyText="No matching entries."
      columnActions={editable ? columnActions : {}}
      on:search={(e) => {
        searchValue = e.detail.value;
        page = 1;
      }}
      on:clearSearch={() => {
        searchValue = "";
        page = 1;
      }}
    >
      <div slot="left-toolbar" class="toolbar_content_switcher">
        <ContentSwitcher options={typeFilterOptions} bind:selected={typeFilter} />
      </div>
      <svelte:fragment slot="cell" let:row let:cell>
        {#if cell.key === "name"}
          <div class="app-website-cell">
            <AppIcon src={appIconUrl(row)} name={row.name} />
            <span>{cell.value || ""}</span>
          </div>
        {:else if cell.key === "appname"}
          {row.isbrowser ? row.windowtitleorurl : row.appname}
        {:else if cell.key === "windowtitle"}
          {row.isbrowser ? "" : row.windowtitleorurl || ""}
        {:else if cell.key !== "menu"}
          {cell.value || ""}
        {/if}
      </svelte:fragment>
    </DataTableBlack>
  </div>
{/if}

<Modal
  bind:open={editModalOpen}
  modalHeading={editEntry?.isbrowser ? "Edit website" : "Edit app"}
  primaryButtonText="Save"
  primaryButtonAction={saveEdit}
  primaryButtonValidations={!!editBlockedReason}
  primaryButtonDisabledTooltip={editBlockedReason}
  secondaryButtonAction={closeEdit}
  on:close={closeEdit}
  actions
>
  {#if editEntry}
    <div class="edit-form">
      <TextInput labelText="Name" bind:value={editEntry.name} />
      {#if editEntry.isbrowser}
        <TextInput
          labelText="Website or sub-URL"
          placeholder="e.g. amazon.com/music"
          bind:value={editEntry.windowtitleorurl}
        />
      {:else}
        <TextInput
          labelText="Application name"
          placeholder="e.g. mspaint"
          bind:value={editEntry.appname}
        />
        <TextInput
          labelText="Window title (optional)"
          bind:value={editEntry.windowtitleorurl}
        />
      {/if}
      <TextInput labelText="Reason (optional)" bind:value={editEntry.reason} />
      {#if editError}
        <InfoBlock type="error" message={editError} showClose={false} />
      {/if}
    </div>
  {/if}
</Modal>

<DangerModal
  danger
  bind:open={deleteConfirmationOpen}
  preventCloseOnClickOutside
  modalHeading="Remove participant exclusion?"
  primaryButtonText="Remove"
  secondaryButtonText="Cancel"
  on:click:button--primary={confirmDelete}
  on:click:button--secondary={closeDeleteConfirmation}
  on:close={closeDeleteConfirmation}
>
  <p>
    This exclusion was defined by a participant. Only remove it after agreeing
    with the employees concerned.
  </p>
  <p><strong>{entryToDelete?.name || "-"}</strong></p>
</DangerModal>

<style>
  .edit-form {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .app-website-cell {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }

</style>
